/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */
/**
 * Defines values for ProvisioningState.
 * Possible values include: 'Creating', 'Updating', 'Deleting', 'Succeeded', 'Failed', 'Locked'
 * @readonly
 * @enum {string}
 */
export var ProvisioningState;
(function (ProvisioningState) {
  /**
   * Resource is in the process of being created.
   */
  ProvisioningState["Creating"] = "Creating";
  /**
   * New property values are being applied to the resource.
   */
  ProvisioningState["Updating"] = "Updating";
  /**
   * Resource is in the process of being deleted.
   */
  ProvisioningState["Deleting"] = "Deleting";
  /**
   * Resource is in healthy state after creation or update operation.
   */
  ProvisioningState["Succeeded"] = "Succeeded";
  /**
   * Previous operation on the resource has failed leaving resource in unhealthy state.
   */
  ProvisioningState["Failed"] = "Failed";
  /**
   * The resource is locked and changes are currently blocked. This could be due to maintenance or
   * a scheduled operation. The state will go back to succeeded once the locking operation has
   * finished.
   */
  ProvisioningState["Locked"] = "Locked";
})(ProvisioningState || (ProvisioningState = {}));
/**
 * Defines values for EnableState.
 * Possible values include: 'Enabled', 'Disabled'
 * @readonly
 * @enum {string}
 */
export var EnableState;
(function (EnableState) {
  EnableState["Enabled"] = "Enabled";
  EnableState["Disabled"] = "Disabled";
})(EnableState || (EnableState = {}));
/**
 * Defines values for ConnectionType.
 * Possible values include: 'Public', 'Private', 'None'
 * @readonly
 * @enum {string}
 */
export var ConnectionType;
(function (ConnectionType) {
  ConnectionType["Public"] = "Public";
  ConnectionType["Private"] = "Private";
  ConnectionType["None"] = "None";
})(ConnectionType || (ConnectionType = {}));
/**
 * Defines values for OsType.
 * Possible values include: 'Windows', 'Linux'
 * @readonly
 * @enum {string}
 */
export var OsType;
(function (OsType) {
  OsType["Windows"] = "Windows";
  OsType["Linux"] = "Linux";
})(OsType || (OsType = {}));
/**
 * Defines values for OsState.
 * Possible values include: 'Generalized', 'Specialized'
 * @readonly
 * @enum {string}
 */
export var OsState;
(function (OsState) {
  /**
   * Image does not contain any machine and user specific information.
   */
  OsState["Generalized"] = "Generalized";
  /**
   * Image contains machine and user specific information.
   */
  OsState["Specialized"] = "Specialized";
})(OsState || (OsState = {}));
/**
 * Defines values for ShutdownOnIdleMode.
 * Possible values include: 'None', 'UserAbsence', 'LowUsage'
 * @readonly
 * @enum {string}
 */
export var ShutdownOnIdleMode;
(function (ShutdownOnIdleMode) {
  /**
   * The VM won't be shut down when it is idle.
   */
  ShutdownOnIdleMode["None"] = "None";
  /**
   * The VM will be considered as idle when there is no keyboard or mouse input.
   */
  ShutdownOnIdleMode["UserAbsence"] = "UserAbsence";
  /**
   * The VM will be considered as idle when user is absent and the resource (CPU and disk)
   * consumption is low.
   */
  ShutdownOnIdleMode["LowUsage"] = "LowUsage";
})(ShutdownOnIdleMode || (ShutdownOnIdleMode = {}));
/**
 * Defines values for CreatedByType.
 * Possible values include: 'User', 'Application', 'ManagedIdentity', 'Key'
 * @readonly
 * @enum {string}
 */
export var CreatedByType;
(function (CreatedByType) {
  CreatedByType["User"] = "User";
  CreatedByType["Application"] = "Application";
  CreatedByType["ManagedIdentity"] = "ManagedIdentity";
  CreatedByType["Key"] = "Key";
})(CreatedByType || (CreatedByType = {}));
/**
 * Defines values for ResourceIdentityType.
 * Possible values include: 'SystemAssigned', 'UserAssigned', 'SystemAssigned,UserAssigned', 'None'
 * @readonly
 * @enum {string}
 */
export var ResourceIdentityType;
(function (ResourceIdentityType) {
  ResourceIdentityType["SystemAssigned"] = "SystemAssigned";
  ResourceIdentityType["UserAssigned"] = "UserAssigned";
  ResourceIdentityType["SystemAssignedUserAssigned"] = "SystemAssigned,UserAssigned";
  ResourceIdentityType["None"] = "None";
})(ResourceIdentityType || (ResourceIdentityType = {}));
/**
 * Defines values for LabState.
 * Possible values include: 'Draft', 'Publishing', 'Scaling', 'Syncing', 'Published'
 * @readonly
 * @enum {string}
 */
export var LabState;
(function (LabState) {
  /**
   * The lab is currently in draft (has not been published).
   */
  LabState["Draft"] = "Draft";
  /**
   * The lab is publishing.
   */
  LabState["Publishing"] = "Publishing";
  /**
   * The lab is scaling.
   */
  LabState["Scaling"] = "Scaling";
  /**
   * The lab is syncing users.
   */
  LabState["Syncing"] = "Syncing";
  /**
   * The lab has been published.
   */
  LabState["Published"] = "Published";
})(LabState || (LabState = {}));
/**
 * Defines values for CreateOption.
 * Possible values include: 'Image', 'TemplateVM'
 * @readonly
 * @enum {string}
 */
export var CreateOption;
(function (CreateOption) {
  /**
   * An image is used to create all lab user virtual machines. When this option is set, no template
   * VM will be created.
   */
  CreateOption["Image"] = "Image";
  /**
   * A template VM will be used to create all lab user virtual machines.
   */
  CreateOption["TemplateVM"] = "TemplateVM";
})(CreateOption || (CreateOption = {}));
/**
 * Defines values for SkuTier.
 * Possible values include: 'Free', 'Basic', 'Standard', 'Premium'
 * @readonly
 * @enum {string}
 */
export var SkuTier;
(function (SkuTier) {
  SkuTier["Free"] = "Free";
  SkuTier["Basic"] = "Basic";
  SkuTier["Standard"] = "Standard";
  SkuTier["Premium"] = "Premium";
})(SkuTier || (SkuTier = {}));
/**
 * Defines values for RegistrationState.
 * Possible values include: 'Registered', 'NotRegistered'
 * @readonly
 * @enum {string}
 */
export var RegistrationState;
(function (RegistrationState) {
  /**
   * User has not yet registered with the lab.
   */
  RegistrationState["Registered"] = "Registered";
  /**
   * User has registered with the lab.
   */
  RegistrationState["NotRegistered"] = "NotRegistered";
})(RegistrationState || (RegistrationState = {}));
/**
 * Defines values for InvitationState.
 * Possible values include: 'NotSent', 'Sending', 'Sent', 'Failed'
 * @readonly
 * @enum {string}
 */
export var InvitationState;
(function (InvitationState) {
  /**
   * The invitation has not been sent.
   */
  InvitationState["NotSent"] = "NotSent";
  /**
   * Currently sending the invitation.
   */
  InvitationState["Sending"] = "Sending";
  /**
   * The invitation has been successfully sent.
   */
  InvitationState["Sent"] = "Sent";
  /**
   * There was an error while sending the invitation.
   */
  InvitationState["Failed"] = "Failed";
})(InvitationState || (InvitationState = {}));
/**
 * Defines values for VirtualMachineState.
 * Possible values include: 'Stopped', 'Starting', 'Running', 'Stopping', 'ResettingPassword',
 * 'Reimaging', 'Redeploying'
 * @readonly
 * @enum {string}
 */
export var VirtualMachineState;
(function (VirtualMachineState) {
  /**
   * The VM is currently stopped.
   */
  VirtualMachineState["Stopped"] = "Stopped";
  /**
   * The VM is starting.
   */
  VirtualMachineState["Starting"] = "Starting";
  /**
   * The VM is running.
   */
  VirtualMachineState["Running"] = "Running";
  /**
   * The VM is stopping.
   */
  VirtualMachineState["Stopping"] = "Stopping";
  /**
   * The VM password is being reset.
   */
  VirtualMachineState["ResettingPassword"] = "ResettingPassword";
  /**
   * The VM is being reimaged.
   */
  VirtualMachineState["Reimaging"] = "Reimaging";
  /**
   * The VM is being redeployed.
   */
  VirtualMachineState["Redeploying"] = "Redeploying";
})(VirtualMachineState || (VirtualMachineState = {}));
/**
 * Defines values for VirtualMachineType.
 * Possible values include: 'User', 'Template'
 * @readonly
 * @enum {string}
 */
export var VirtualMachineType;
(function (VirtualMachineType) {
  /**
   * A user VM
   */
  VirtualMachineType["User"] = "User";
  /**
   * A template VM
   */
  VirtualMachineType["Template"] = "Template";
})(VirtualMachineType || (VirtualMachineType = {}));
/**
 * Defines values for RecurrenceFrequency.
 * Possible values include: 'Daily', 'Weekly'
 * @readonly
 * @enum {string}
 */
export var RecurrenceFrequency;
(function (RecurrenceFrequency) {
  /**
   * Schedule will run every days.
   */
  RecurrenceFrequency["Daily"] = "Daily";
  /**
   * Schedule will run every week on days specified in weekDays.
   */
  RecurrenceFrequency["Weekly"] = "Weekly";
})(RecurrenceFrequency || (RecurrenceFrequency = {}));
/**
 * Defines values for WeekDay.
 * Possible values include: 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday',
 * 'Saturday'
 * @readonly
 * @enum {string}
 */
export var WeekDay;
(function (WeekDay) {
  /**
   * Schedule will run on Sunday
   */
  WeekDay["Sunday"] = "Sunday";
  /**
   * Schedule will run on Monday
   */
  WeekDay["Monday"] = "Monday";
  /**
   * Schedule will run on Tuesday
   */
  WeekDay["Tuesday"] = "Tuesday";
  /**
   * Schedule will run on Wednesday
   */
  WeekDay["Wednesday"] = "Wednesday";
  /**
   * Schedule will run on Thursday
   */
  WeekDay["Thursday"] = "Thursday";
  /**
   * Schedule will run on Friday
   */
  WeekDay["Friday"] = "Friday";
  /**
   * Schedule will run on Saturday
   */
  WeekDay["Saturday"] = "Saturday";
})(WeekDay || (WeekDay = {}));
/**
 * Defines values for OperationStatus.
 * Possible values include: 'NotStarted', 'InProgress', 'Succeeded', 'Failed', 'Canceled'
 * @readonly
 * @enum {string}
 */
export var OperationStatus;
(function (OperationStatus) {
  /**
   * The operation has been accepted but hasn't started.
   */
  OperationStatus["NotStarted"] = "NotStarted";
  /**
   * The operation is running
   */
  OperationStatus["InProgress"] = "InProgress";
  /**
   * The operation Succeeded
   */
  OperationStatus["Succeeded"] = "Succeeded";
  /**
   * The operation failed
   */
  OperationStatus["Failed"] = "Failed";
  /**
   * Not supported yet
   */
  OperationStatus["Canceled"] = "Canceled";
})(OperationStatus || (OperationStatus = {}));
/**
 * Defines values for UsageUnit.
 * Possible values include: 'Count'
 * @readonly
 * @enum {string}
 */
export var UsageUnit;
(function (UsageUnit) {
  UsageUnit["Count"] = "Count";
})(UsageUnit || (UsageUnit = {}));
/**
 * Defines values for LabServicesSkuTier.
 * Possible values include: 'Standard', 'Premium'
 * @readonly
 * @enum {string}
 */
export var LabServicesSkuTier;
(function (LabServicesSkuTier) {
  LabServicesSkuTier["Standard"] = "Standard";
  LabServicesSkuTier["Premium"] = "Premium";
})(LabServicesSkuTier || (LabServicesSkuTier = {}));
/**
 * Defines values for ScaleType.
 * Possible values include: 'None', 'Manual', 'Automatic'
 * @readonly
 * @enum {string}
 */
export var ScaleType;
(function (ScaleType) {
  /**
   * The capacity is not adjustable in any way.
   */
  ScaleType["None"] = "None";
  /**
   * The user must manually scale this SKU in and out.
   */
  ScaleType["Manual"] = "Manual";
  /**
   * The user is permitted to scale this SKU in and out.
   */
  ScaleType["Automatic"] = "Automatic";
})(ScaleType || (ScaleType = {}));
/**
 * Defines values for RestrictionType.
 * Possible values include: 'Location'
 * @readonly
 * @enum {string}
 */
export var RestrictionType;
(function (RestrictionType) {
  RestrictionType["Location"] = "Location";
})(RestrictionType || (RestrictionType = {}));
/**
 * Defines values for RestrictionReasonCode.
 * Possible values include: 'QuotaId', 'NotAvailableForSubscription'
 * @readonly
 * @enum {string}
 */
export var RestrictionReasonCode;
(function (RestrictionReasonCode) {
  RestrictionReasonCode["QuotaId"] = "QuotaId";
  RestrictionReasonCode["NotAvailableForSubscription"] = "NotAvailableForSubscription";
})(RestrictionReasonCode || (RestrictionReasonCode = {}));
/**
 * Defines values for Origin.
 * Possible values include: 'user', 'system', 'user,system'
 * @readonly
 * @enum {string}
 */
export var Origin;
(function (Origin) {
  Origin["User"] = "user";
  Origin["System"] = "system";
  Origin["Usersystem"] = "user,system";
})(Origin || (Origin = {}));
/**
 * Defines values for ActionType.
 * Possible values include: 'Internal'
 * @readonly
 * @enum {string}
 */
export var ActionType;
(function (ActionType) {
  ActionType["Internal"] = "Internal";
})(ActionType || (ActionType = {}));